import * as React from 'react';
import axios, { CancelTokenStatic } from 'axios';
import clsx from 'clsx';
import { makeStyles, useMediaQuery } from '@material-ui/core';
import { UserStateContext } from '../../providers/UserStateContextProvider';
import { CheckOutStateContext } from '../../providers/CheckOutStateContextProvider';
import { PaymentMethodsAccepted } from '../../models/payment/PaymentMethodsAccepted';
import { ElementDimensions } from '../../styles/ElementDimensions';
import ErrorDialog, { ErrorDialogProps } from '../common/ErrorDialog';
import { String_PayCheck_PaymentError, String_System_ConnectionError, String_System_GeneralError,
    String_PayCheck_CreditCardButton, 
    String_PayCheck_RoomChargeButton} from '../../utilities/StringHelper';
import { CheckStateContext } from '../../providers/CheckStateContextProvider';
import { PaymentDigitalWalletType } from '../../models/payment/PaymentDigitalWalletType';
import PaymentTerms from './PaymentTerms';
import { PMSType } from '../../models/common/PMSType';
import { CreatePaymentRequest } from '../../models/payment/CreatePaymentRequest';
import { CreatePaymentResponse } from '../../models/payment/CreatePaymentResponse';
import { PaymentMethod } from '../../models/payment/PaymentMethod';

const useStyles = makeStyles({
    iframe: {
        width: '100vw',
        height: '100%',
        borderWidth: '0',
    },
    iframeLandscape: {
        width: 'calc(100vw * .7)',
        height: '100%',
        borderWidth: '0',
    },
    root: {
        textAlign: 'center',
        height: 'calc(100vh - ' + (73 + ElementDimensions.appBarHeight + ElementDimensions.stepperHeight + ElementDimensions.footerBarHeightPaymentScreen) + 'px)',
    },
    rootLandscape: {
        textAlign: 'center',
        height: '150%',
    },
});

export type Shift4SuccessHandler = (data: i4GoSuccessData) => any;

export interface i4GoSuccessData {
    postalCode: string,
    token: string,
    extendedCardData: string,
    paymentMethod: PaymentMethod | null,
    paymentDigitalWalletType: PaymentDigitalWalletType
}

export interface Shift4PaymentProps {
    paymentMethodAllowed: PaymentMethodsAccepted | undefined,
    total: number,
    successHandler: Shift4SuccessHandler
}

export default function Shift4Payment(props: Shift4PaymentProps) {
    const { paymentMethodAllowed, total, successHandler } = props;
    const isPortrait = true;
    const [isAuthorized, setIsAuthorized] = React.useState<boolean>(false);
    const [accessBlock, setAccessBlock] = React.useState<string>('');
    const [server, setServer] = React.useState<string>('');
    const [acceptedPaymentTypes, setAcceptedPaymentTypes] = React.useState('');
    const [errorDialogOpen, setErrorDialogOpen] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>(''); 
    const [isLoaded, setIsLoaded] = React.useState<boolean>(false);
    const userState = React.useContext(UserStateContext);
    const checkOutState = React.useContext(CheckOutStateContext);
    const checkState = React.useContext(CheckStateContext);
    const classes = useStyles({ primary_main_color: checkOutState.state.skin.primary_main_color, 
        primary_light_color: checkOutState.state.skin.primary_light_color, primary_dark_color: checkOutState.state.skin.primary_dark_color });

    React.useEffect(() => {
        const CancelToken = axios.CancelToken;
        const source = CancelToken.source();

        if (isAuthorized === false) {
            window.addEventListener("message", messageHandler);
            authorize(CancelToken);
        }
        
        return () => {
            CancelToken.source().cancel();
            window.removeEventListener("message", messageHandler);
        };
    }, []);

    const messageHandler = (event: { data: string; }) => {
        try{
            var data = JSON.parse(event.data);
            if (data.action === "i4GoSuccess") {
                let paymentMethodUsed: PaymentMethod | null = null;
                let paymentDigitalWalletType: PaymentDigitalWalletType = PaymentDigitalWalletType.None;
    
                if (data.paymentMethod === "ApplePay" || data.paymentMethod === "GooglePay") {
                    if (data.paymentMethod === "ApplePay") {
                        paymentDigitalWalletType = PaymentDigitalWalletType.ApplePay;
                    }
                    else {
                        paymentDigitalWalletType = PaymentDigitalWalletType.GooglePay;
                    }
                }
                else {
                    if (data.data.i4go_cardtype === 'VS') {
                        paymentMethodUsed = PaymentMethod.Visa;
                    }
                    else if (data.data.i4go_cardtype === 'MC') {
                        paymentMethodUsed = PaymentMethod.Mastercard;
                    }
                    else if (data.data.i4go_cardtype === 'AX') {
                        paymentMethodUsed = PaymentMethod.Amex;
                    }
                    else if (data.data.i4go_cardtype === 'DC') {
                        paymentMethodUsed = PaymentMethod.Discover;
                    } 
                }                
                
                const i4GoSuccessData: i4GoSuccessData = {
                    /*postalCode: data.data.i4go_postalcode,*/
                    postalCode: '',
                    token: data.data.i4go_uniqueid,
                    extendedCardData: data.data.i4go_extendedcarddata,
                    paymentMethod: paymentMethodUsed,
                    paymentDigitalWalletType: paymentDigitalWalletType
                }
    
                successHandler(i4GoSuccessData);
            }
            else if (data.action === "i4GoFailure") {
                setErrorMessage(String_PayCheck_PaymentError.find(s => s.key === userState.state.locale)!!.value);
                setErrorDialogOpen(true);
            }
            else if (data.action === "i4GoSubmit") {
                var postData = {
                    action: "i4GoSubmit"
                }
                // @ts-ignore
                document.getElementById('i4GoFrame').contentWindow.postMessage(JSON.stringify(postData), "*");
            }
            else if (data.action === "i4GoReady") {
                setIsLoaded(true);
            }
        }
        catch (exception) {

        }
    }

    const authorize = (cancelToken: CancelTokenStatic) => {

        const request: CreatePaymentRequest = {
            total: total,
            user_id: userState.state.userId!!,
            table_id: checkOutState.state.tableId,
            check_please_payment_id: checkState.state.paymentId!!
        };

        axios.post(process.env.REACT_APP_CHECKOUT_API_URL + '/payment/create/', request, { cancelToken: cancelToken.source().token })
        .then(function(response) {
            let r = response.data as CreatePaymentResponse;

            if (r.success === true) {
                setAccessBlock(r.i4go_accessblock!!);
                setServer(r.i4go_server!!);
                
                if (paymentMethodAllowed !== undefined) {
                    let hasVisa = paymentMethodAllowed & PaymentMethodsAccepted.Visa;
                    let hasMastercard = paymentMethodAllowed & PaymentMethodsAccepted.Mastercard;
                    let hasAmex = paymentMethodAllowed & PaymentMethodsAccepted.Amex;
                    let hasDiscover = paymentMethodAllowed & PaymentMethodsAccepted.Discover;
                    var paymentOptionsString = "";
                    if (hasVisa) {
                        paymentOptionsString += "VS,"
                    }
    
                    if (hasMastercard) {
                        paymentOptionsString += "MC,"
                    }
    
                    if (hasAmex) {
                        paymentOptionsString += "AX,"
                    }
    
                    if (hasDiscover) {
                        paymentOptionsString += "NS,"
                    }
    
                    paymentOptionsString = paymentOptionsString.substring(0, paymentOptionsString.length - 1);
                    setAcceptedPaymentTypes(paymentOptionsString);
                }
                
                setIsAuthorized(true);
            }
            else {
                setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                setErrorDialogOpen(true);
            }
        })
        .catch(function(error) {
            if (error.response) {
                setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
            }
            else {
                setErrorMessage(String_System_ConnectionError.find(s => s.key === userState.state.locale)!!.value);
            }

            setErrorDialogOpen(true);
        });
    };

    const iFrameLoaded = () => {

        const data = {
            action: "initi4Go",
            server: server,
            accessBlock: accessBlock,
            acceptedPayments: acceptedPaymentTypes,
            logoUrl: checkOutState.state.restaurantLoaderUrl,
            creditCardButton: String_PayCheck_CreditCardButton.find(s => s.key === userState.state.locale)!!.value,
            roomChargeButton: String_PayCheck_RoomChargeButton.find(s => s.key === userState.state.locale)!!.value,
            allowRoomCharge: paymentMethodAllowed!! & PaymentMethodsAccepted.RoomCharge ? 'allow' : 'none',
        }

        // @ts-ignore
        document.getElementById('i4GoFrame').contentWindow.postMessage(JSON.stringify(data), "*");
    }

    const handleErrorDialogClose = (event: React.MouseEvent<HTMLElement>) => {
        setErrorDialogOpen(false);
    }

    const errorDialogProps: ErrorDialogProps = {
        open: errorDialogOpen,
        message: errorMessage,
        tryAgain: false,
        onCloseHandler: handleErrorDialogClose,
    }

    return (
        <div className={clsx({
            [classes.root]: isPortrait,
            [classes.rootLandscape]: !isPortrait,
        })}>

            {isLoaded && 
                <PaymentTerms />
            }            

            {isAuthorized &&
            // @ts-ignore
                <iframe 
                    // @ts-ignore
                    allowpaymentrequest="true"
                    id="i4GoFrame"
                    onLoad={iFrameLoaded}
                    src={checkOutState.state.restaurantName!!.indexOf('CheckPlease') !== -1 ?
                        process.env.REACT_APP_BASE_URL + "/shift4/Dev.Shift4.html?color=" + 
                            checkOutState.state.skin.primary_main_color.substring(1) :                    
                        process.env.REACT_APP_SHIFT4_URL + "?color=" + 
                            checkOutState.state.skin.primary_main_color.substring(1)}
                    className={clsx({
                        [classes.iframe]: isPortrait,
                        [classes.iframeLandscape]: !isPortrait,
                })}></iframe>
            }

            <ErrorDialog
                open={errorDialogProps.open}
                message={errorDialogProps.message}
                tryAgain={errorDialogProps.tryAgain}
                onCloseHandler={errorDialogProps.onCloseHandler} />
        </div>
        
    );
}